// Utilitários do modelo "Horizonte" (topo 11 · busca 14 · card 8 · rodapé 8).
// Só as 4 peças do Horizonte importam daqui — não é componente compartilhado dos ~300 sites.
import { useEffect, useState } from 'react';
import { useReducedMotion } from 'framer-motion';
import { ensureReadableTextColor, isColorLight } from '../../../utils/FunctionsGlobals';
import { resolveSiteWhatsAppUrl } from '../../../utils/site2524WhatsApp';

/**
 * O site está no modelo "Horizonte"? A assinatura é o TOPO 11: é a peça presente em todas as
 * páginas (home e internas) e a que define a cara do site. Quem liga o Horizonte pelo
 * /gestao/modelos recebe as 4 peças; quem escolhe só o topo 11 no Builder também ganha a
 * linguagem do modelo nas internas (/imoveis, ficha) e nos blocos da home (blog, canal,
 * equipe, empresa) — por design, sem número novo no catálogo do app. Nenhum outro modelo
 * ou tenant é afetado (zero diff com modelo_topo ≠ 11).
 */
export const ehModeloHorizonte = (site: any): boolean => Number(site?.modelo_topo) === 11;

// Curva "cinematográfica" usada em todas as entradas do modelo (desacelera longo no fim).
export const EASE_HORIZONTE = [0.22, 1, 0.36, 1] as const;

/**
 * Preferência de movimento reduzido SEM divergir do SSR: no servidor e na 1ª renderização do
 * cliente devolve `false` (o HTML e a hidratação batem); depois de montar, o valor real do
 * `prefers-reduced-motion`. Assim nenhum atributo muda entre SSR e hidratação.
 */
export function useMovimentoReduzido(): boolean {
    const prefere = useReducedMotion();
    const [montado, setMontado] = useState(false);
    useEffect(() => setMontado(true), []);
    return montado ? Boolean(prefere) : false;
}

// hex (#rgb/#rrggbb) → [r, g, b]; inválido → null.
const hexParaRgb = (hex: string | undefined | null): [number, number, number] | null => {
    if (!hex) return null;
    let h = String(hex).trim().replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return null;
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
};

/** hex → rgba(); hex inválido cai no `reserva`. */
export const hexParaRgba = (hex: string | undefined | null, alpha: number, reserva = 'rgba(15, 18, 26, ALPHA)'): string => {
    const rgb = hexParaRgb(hex);
    if (!rgb) return reserva.replace('ALPHA', String(alpha));
    return `rgba(${rgb[0]}, ${rgb[1]}, ${rgb[2]}, ${alpha})`;
};

/** Mistura duas cores hex (t = 0 → a; t = 1 → b). Inválida → devolve a outra (ou `a`). */
export const misturarCores = (a: string | undefined | null, b: string | undefined | null, t: number): string => {
    const ra = hexParaRgb(a);
    const rb = hexParaRgb(b);
    if (!ra) return b || a || '#000000';
    if (!rb) return a || '#000000';
    const c = (i: number) => Math.round(ra[i] + (rb[i] - ra[i]) * t).toString(16).padStart(2, '0');
    return `#${c(0)}${c(1)}${c(2)}`;
};

/** Link de WhatsApp com a mensagem do tenant (mesmo texto dos topos/rodapés 7 e Aurora). */
export const linkWhatsApp = (site: any, phone?: string): string => {
    const clean = (phone || '').replace(/\D/g, '');
    if (!clean) return '';
    const text =
        site?.texto_botao_whats ||
        `Olá, estou visitando o site da ${site?.Dados?.empresa || ''}, gostaria de um atendimento personalizado. Pode me ajudar?`;
    return `https://api.whatsapp.com/send?phone=55${clean}&text=${encodeURIComponent(text)}`;
};

/**
 * WhatsApp dos TELEFONES DO TENANT (tel1..tel4) no topo/rodapé dos modelos 7–11: passa pelo
 * redirecionador de rastreio por site (utils/site2524WhatsApp — hoje só o cs2524), igual ao
 * Topo/AboutPieces1 clássicos. Nos demais sites devolve exatamente o linkWhatsApp.
 * NÃO usar em filial/corretor/flutuante/"Anuncie" (no clássico esses não passam pelo redirecionador).
 */
export const linkWhatsAppDoTenant = (site: any, phone?: string): string => {
    const url = linkWhatsApp(site, phone);
    return url ? resolveSiteWhatsAppUrl(site, url) : url;
};

export type TelefoneTenant = { tel?: string; nome?: string; whats?: any };

/** Telefones cadastrados (tel1..telN) e o CTA: 1º com WhatsApp; sem WhatsApp, o 1º vira ligação. */
export const contatosDoTenant = (site: any, quantos = 4) => {
    const todos: TelefoneTenant[] = [
        { tel: site?.Dados?.tel1, nome: site?.Dados?.tel1_nome, whats: site?.Dados?.tel1_whats },
        { tel: site?.Dados?.tel2, nome: site?.Dados?.tel2_nome, whats: site?.Dados?.tel2_whats },
        { tel: site?.Dados?.tel3, nome: site?.Dados?.tel3_nome, whats: site?.Dados?.tel3_whats },
        { tel: site?.Dados?.tel4, nome: site?.Dados?.tel4_nome, whats: site?.Dados?.tel4_whats },
    ];
    const phones = todos.slice(0, quantos).filter((p) => p.tel);
    const whatsPhone = phones.find((p) => p.whats);
    const ctaPhone = whatsPhone || phones[0];
    const ctaIsWhats = Boolean(whatsPhone);
    const ctaHref = ctaPhone ? (ctaIsWhats ? linkWhatsAppDoTenant(site, ctaPhone.tel) : `tel:+55${(ctaPhone.tel || '').replace(/\D/g, '')}`) : '';
    return { phones, ctaPhone, ctaIsWhats, ctaHref };
};

/** Compara caminhos ignorando query/hash e barra final. */
export const mesmaRota = (asPath: string, href: string): boolean => {
    const limpa = (v: string) => v.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/';
    return limpa(asPath) === limpa(href);
};

export type ItemMenu = { label: string; href: string; external?: boolean };

/** Menu do topo: transações + menus disponíveis do tenant + blog (mesma origem do topo 10). */
export const menuDoTenant = (site: any, allDataHome: any): ItemMenu[] => {
    const itens: ItemMenu[] = [];
    (site?.transacoes || []).forEach((t: string) => {
        if (!t) return;
        const low = String(t).toLowerCase();
        const label = low === 'venda' ? 'Comprar' : low === 'aluguel' ? 'Alugar' : low === 'temporada' ? 'Temporada' : t;
        itens.push({ label, href: `/imoveis/${low}/-/-/-/-` });
    });
    (allDataHome?.menus || [])
        .filter((m: any) => m?.Disponivel && m?.titulo)
        .slice(0, 8)
        .forEach((m: any) => {
            itens.push({
                label: m.titulo,
                href: m?.link ? m.link : `/menu/${m.titulo}`,
                external: Boolean(m?.link && !String(m.link).startsWith('/')),
            });
        });
    if (allDataHome?.blogs?.[0]?.id) itens.push({ label: 'Blog', href: '/blog' });
    return itens;
};

/** Redes sociais cadastradas do tenant (ordem fixa). */
export const redesDoTenant = (site: any): { href: string; rede: string }[] =>
    [
        { href: site?.Dados?.instagram, rede: 'Instagram' },
        { href: site?.Dados?.facebook, rede: 'Facebook' },
        { href: site?.Dados?.youtube, rede: 'Youtube' },
        { href: site?.Dados?.tiktok, rede: 'Tiktok' },
        { href: site?.Dados?.Linkedin, rede: 'Linkedin' },
    ].filter((s) => s.href);

/**
 * CSS vars (--hz-*) das páginas INTERNAS do Horizonte (/imoveis, ficha, /condominios, /empreendimento):
 * as MESMAS contas do varsSecao do CardHorizonte com fundo = cor5 — texto legível (cor11/cor13),
 * acento (cor2) legível sobre o fundo, sutil/sutil-forte/painel = texto a 0.14/0.28/0.06,
 * --hz-texto/--hz-fundo para a pílula sólida. Fora do gate ehModeloHorizonte: null (zero diff).
 */
export const varsHorizonteInterna = (site: any): Record<string, string> | null => {
    if (!ehModeloHorizonte(site)) return null;
    const corFundo = site?.cor5 || '#f5f4f1';
    const corTitulo = ensureReadableTextColor(isColorLight(corFundo) ? site?.cor11 : site?.cor13, corFundo || '#ffffff') || '#141821';
    const corAcento = ensureReadableTextColor(site?.cor2, corFundo, 3.0, corTitulo, corTitulo) || corTitulo;
    return {
        color: corTitulo,
        '--hz-texto': corTitulo,
        '--hz-fundo': corFundo,
        '--hz-acento': corAcento,
        '--hz-sutil': hexParaRgba(corTitulo, 0.14),
        '--hz-sutil-forte': hexParaRgba(corTitulo, 0.28),
        '--hz-painel': hexParaRgba(corTitulo, 0.06),
    };
};
